// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
import type { ValueCorrectionFieldKey } from "./valueCorrectionFields";
import { IconDots, IconHistory } from "@tabler/icons-react";
import { useRef } from "react";
import { Button, Dropdown } from "../ui";

export type PayrollAuditContext = {
  month: string;
  periodThrough: string;
  employeeId?: string | number | null;
  userId?: string | number | null;
  fieldKey?: ValueCorrectionFieldKey | null;
};

type PayrollAuditKind = "business" | "technical" | "changes";

export type { PayrollAuditKind };

export const openPayrollAuditOverlay = (
  kind: PayrollAuditKind,
  context: PayrollAuditContext,
  trigger?: HTMLElement | null,
  replace = false,
) => {
  const params = new URLSearchParams(window.location.search);
  if (context.month) params.set("month", context.month);
  if (context.periodThrough) {
    params.set("periodThrough", context.periodThrough);
  }
  if (context.employeeId) {
    params.set("employeeId", String(context.employeeId));
  }
  if (context.userId) params.set("userId", String(context.userId));
  if (context.fieldKey) params.set("fieldKey", context.fieldKey);
  const baseParams = new URLSearchParams(params);
  baseParams.delete("auditOverlay");
  const baseUrl = `${window.location.pathname}${baseParams.size ? `?${baseParams.toString()}` : ""}`;
  params.set("auditOverlay", kind);
  const overlayUrl = `${window.location.pathname}?${params.toString()}`;
  const currentState = window.history.state ?? {};
  if (!replace) {
    // Preserve the exact contextual panel (employee/value detail included) as
    // the browser Back destination before adding the audit history entry.
    window.history.replaceState(
      { ...currentState, payrollAuditOverlay: false },
      "",
      baseUrl,
    );
  }
  window.history[replace ? "replaceState" : "pushState"](
    {
      ...currentState,
      payrollAuditOverlay: true,
      payrollAuditOverlayUrl: overlayUrl,
      payrollAuditReturnUrl:
        replace && currentState.payrollAuditReturnUrl
          ? currentState.payrollAuditReturnUrl
          : baseUrl,
    },
    "",
    overlayUrl,
  );
  window.dispatchEvent(
    new CustomEvent("payroll-audit-overlay-change", {
      detail: { kind, trigger },
    }),
  );
};

export function PayrollAuditLinks({
  context,
  className = "",
  compact = false,
  overflow = false,
}: {
  context: PayrollAuditContext;
  className?: string;
  compact?: boolean;
  overflow?: boolean;
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  if (overflow)
    return (
      <div className="payroll-header-more">
        <Dropdown.Root>
          <Button
            ref={trigger}
            type="button"
            variant="ghost"
            iconOnly
            className="payroll-header-utility"
            aria-label="More payroll actions"
          >
            <IconDots size={18} aria-hidden="true" />
          </Button>
          <Dropdown.Popover
            placement="bottom end"
            className="border border-border bg-elevated text-foreground"
          >
            <Dropdown.Menu
              aria-label="Payroll actions"
              onAction={() =>
                openPayrollAuditOverlay("business", context, trigger.current)
              }
            >
              <Dropdown.Item id="audit" textValue="Audit history">
                <IconHistory size={16} aria-hidden="true" />
                <span>Audit history</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown.Root>
      </div>
    );
  return (
    <div
      className={`payroll-audit-links${compact ? " payroll-audit-links--compact" : ""}${className ? ` ${className}` : ""}`}
    >
      <button
        type="button"
        onClick={(event) =>
          openPayrollAuditOverlay("business", context, event.currentTarget)
        }
        aria-label="Open payroll audit"
      >
        <IconHistory size={16} aria-hidden="true" />
        Audit
      </button>
    </div>
  );
}
